import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCookieBite, faShieldHalved, faChartSimple, faBullhorn, faCheck, faTimes, faCog } from '@fortawesome/free-solid-svg-icons';

const CookieBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    } else {
      try {
        const saved = JSON.parse(cookieConsent);
        setPreferences(saved);
      } catch (e) {
        // ignore
      }
    }
  }, []);

  const acceptAll = () => {
    const all = { necessary: true, analytics: true, marketing: true };
    localStorage.setItem('cookieConsent', JSON.stringify(all));
    setPreferences(all);
    setIsVisible(false);
  };

  const rejectAll = () => {
    const rejected = { necessary: true, analytics: false, marketing: false };
    localStorage.setItem('cookieConsent', JSON.stringify(rejected));
    setPreferences(rejected);
    setIsVisible(false);
  };

  const savePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setIsVisible(false);
  };

  const handlePreferenceChange = (key: 'analytics' | 'marketing') => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const bannerVariants = {
    hidden: { y: 100, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: "spring" as const, stiffness: 300, damping: 30 } },
    exit: { y: 100, opacity: 0, transition: { duration: 0.3 } }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[99]"
            onClick={rejectAll}
          />

          {/* Banner */}
          <motion.div
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={bannerVariants}
            className="fixed bottom-0 left-0 right-0 z-[100]"
          >
            <div className="bg-white shadow-2xl border-t border-light-border/50">
              <div className="container mx-auto px-4 py-6 md:py-8">
                <AnimatePresence mode="wait">
                  {!showPreferences ? (
                    <motion.div
                      key="main"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex flex-col lg:flex-row items-start lg:items-center gap-6"
                    >
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <FontAwesomeIcon icon={faCookieBite} className="text-primary text-xl" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-light-text mb-1 font-display">Utilizziamo i cookie</h3>
                          <p className="text-light-muted text-sm leading-relaxed">
                            Questo sito utilizza cookie tecnici necessari al funzionamento e, previo tuo consenso, 
                            cookie analitici e di marketing. Puoi scegliere quali attivare.{' '}
                            <Link to="/cookie-policy" className="text-primary hover:underline font-medium">
                              Cookie Policy
                            </Link>{' · '}
                            <Link to="/privacy-policy" className="text-primary hover:underline font-medium">
                              Privacy Policy
                            </Link>
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-2.5 w-full lg:w-auto flex-shrink-0">
                        <button
                          onClick={() => setShowPreferences(true)}
                          className="px-5 py-2.5 text-sm font-semibold text-light-muted border border-light-border rounded-xl hover:bg-light-background transition-colors flex items-center justify-center gap-2"
                        >
                          <FontAwesomeIcon icon={faCog} className="text-xs" />
                          Personalizza
                        </button>
                        <button
                          onClick={rejectAll}
                          className="px-5 py-2.5 text-sm font-semibold text-light-text border border-light-border rounded-xl hover:bg-light-background transition-colors flex items-center justify-center gap-2"
                        >
                          <FontAwesomeIcon icon={faTimes} className="text-xs" />
                          Rifiuta tutti
                        </button>
                        <button
                          onClick={acceptAll}
                          className="px-6 py-2.5 text-sm font-semibold text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors shadow-btn flex items-center justify-center gap-2"
                        >
                          <FontAwesomeIcon icon={faCheck} className="text-xs" />
                          Accetta tutti
                        </button>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="prefs"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                          <FontAwesomeIcon icon={faCog} className="text-primary" />
                        </div>
                        <h3 className="text-lg font-bold text-light-text font-display">Impostazioni Cookie</h3>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        {/* Necessari */}
                        <div className="p-4 rounded-xl border border-light-border/60 bg-light-background/50">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                <FontAwesomeIcon icon={faShieldHalved} className="text-primary text-sm" />
                              </div>
                              <h4 className="font-semibold text-sm text-light-text">Necessari</h4>
                            </div>
                            <span className="text-xs text-primary font-semibold bg-primary/10 px-2.5 py-1 rounded-full">Sempre attivi</span>
                          </div>
                          <p className="text-light-muted text-xs leading-relaxed">
                            Essenziali per il funzionamento del sito. Non possono essere disattivati.
                          </p>
                        </div>

                        {/* Analitici */}
                        <div className="p-4 rounded-xl border border-light-border/60 bg-light-background/50">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center">
                                <FontAwesomeIcon icon={faChartSimple} className="text-secondary text-sm" />
                              </div>
                              <h4 className="font-semibold text-sm text-light-text">Analitici</h4>
                            </div>
                            <button
                              onClick={() => handlePreferenceChange('analytics')}
                              className={`relative w-11 h-6 rounded-full transition-colors ${
                                preferences.analytics ? 'bg-primary' : 'bg-gray-300'
                              }`}
                            >
                              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                                preferences.analytics ? 'translate-x-5' : 'translate-x-0'
                              }`} />
                            </button>
                          </div>
                          <p className="text-light-muted text-xs leading-relaxed">
                            Ci aiutano a capire come utilizzi il sito per migliorare l'esperienza di navigazione.
                          </p>
                        </div>

                        {/* Marketing */}
                        <div className="p-4 rounded-xl border border-light-border/60 bg-light-background/50">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                                <FontAwesomeIcon icon={faBullhorn} className="text-accent text-sm" />
                              </div>
                              <h4 className="font-semibold text-sm text-light-text">Marketing</h4>
                            </div>
                            <button
                              onClick={() => handlePreferenceChange('marketing')}
                              className={`relative w-11 h-6 rounded-full transition-colors ${
                                preferences.marketing ? 'bg-primary' : 'bg-gray-300'
                              }`}
                            >
                              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                                preferences.marketing ? 'translate-x-5' : 'translate-x-0'
                              }`} />
                            </button>
                          </div>
                          <p className="text-light-muted text-xs leading-relaxed">
                            Utilizzati per mostrarti contenuti e annunci pertinenti ai tuoi interessi.
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row justify-end gap-2.5">
                        <button
                          onClick={() => setShowPreferences(false)}
                          className="px-5 py-2.5 text-sm font-semibold text-light-muted border border-light-border rounded-xl hover:bg-light-background transition-colors"
                        >
                          Indietro
                        </button>
                        <button
                          onClick={rejectAll}
                          className="px-5 py-2.5 text-sm font-semibold text-light-text border border-light-border rounded-xl hover:bg-light-background transition-colors"
                        >
                          Rifiuta opzionali
                        </button>
                        <button
                          onClick={savePreferences}
                          className="px-6 py-2.5 text-sm font-semibold text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors shadow-btn"
                        >
                          Salva preferenze
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
